import type { UseFormReturn } from "react-hook-form";

import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { AutoSuggestedWork, GetMyAccountQuery, GetMyProfileQuery, Sync } from "@/api/queries";

import { usePostSync } from "@/api/mutations";
import { sendEvent } from "@/helpers/helpers";

import type { SyncsApplyFormData } from "../utils/syncsApplySchema";

import { buildSyncPayload } from "../utils/syncsApplyUtils";

type UseSyncsApplyMutationProps = {
  form: UseFormReturn<SyncsApplyFormData>;
  cachedSongs: AutoSuggestedWork[];
  syncsData: Sync[];
  myAccount: GetMyAccountQuery;
  userInfo: GetMyProfileQuery;
  setPending: (v: boolean) => void;
  setMutationErrorMessage: (v: string) => void;
};

type UseSyncsApplyMutationReturn = {
  applySync: (data: SyncsApplyFormData) => Promise<void>;
};

export function useSyncsApplyMutation({
  form,
  cachedSongs,
  syncsData,
  myAccount,
  userInfo,
  setPending,
  setMutationErrorMessage,
}: UseSyncsApplyMutationProps): UseSyncsApplyMutationReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = usePostSync();

  const applySync = async (data: SyncsApplyFormData): Promise<void> => {
    setPending(true);
    setMutationErrorMessage("");
    form.clearErrors("root");

    try {
      createMutation.reset();
      const payload = buildSyncPayload(
        data,
        cachedSongs,
        syncsData,
        myAccount.data,
        userInfo.data,
      );

      const response = await createMutation.mutateAsync(payload);

      if (response.failedvalidation === true) {
        const messages = response.messages?.join(", ") ?? "";
        form.setError("root", { message: messages });
        return;
      }

      toast.success(t("other:successfully-submitted-sync-toast"));
      void navigate({ to: "/syncs" });
      sendEvent({ property: "pe14489857_sync_brief_applied_for" });
    }
    catch (error) {
      setMutationErrorMessage(error instanceof Error ? error.message : String(error));
    }
    finally {
      setPending(false);
    }
  };

  return { applySync };
}
